import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ScoreTimeSeriesService } from 'src/app/shared/services/data-api/score-time-series.service';
import { ScoreUtilsService } from 'src/app/shared/utils/score-utils.service';
import { ScoreRequestTypes } from 'src/app/shared/models/score-time-series.interface';
import {
  NewsScoreStateService,
  EntitySentimentResponse,
  FetchEntitySentimentOptions,
} from '../news-score-state.service';

export interface ScoreTimeSeriesResult {
  score_ts: Array<{ date: string; score: number | null }>;
  score_ts_last_score: { score: number | null } | null;
}

export interface SentimentTimeSeriesResult {
  quotient_ts: Array<{ date: string; quotient: number | null }>;
}

@Injectable({
  providedIn: 'root',
})
export class NewsChartScoreService {
  readonly scoreLoading$ = this.newsScoreState.loading$;
  readonly scoreState$ = this.newsScoreState.scoreState$;
  readonly scoreError$ = this.newsScoreState.error$;

  constructor(
    private scoreTimeSeriesService: ScoreTimeSeriesService,
    private scoreUtils: ScoreUtilsService,
    private newsScoreState: NewsScoreStateService,
  ) {}

  fetchScoreTimeSeries(
    scoreType: ScoreRequestTypes,
    time?: string,
  ): Observable<ScoreTimeSeriesResult> {
    if (time) {
      return this.newsScoreState.fetchScoreDataWithTime(scoreType, time).pipe(
        map((state) => ({
          score_ts: state.scoreData,
          score_ts_last_score:
            state.lastScore !== null ? { score: state.lastScore } : null,
        })),
      );
    }

    return this.newsScoreState.fetchScoreData(scoreType).pipe(
      map((state) => ({
        score_ts: state.scoreData,
        score_ts_last_score:
          state.lastScore !== null ? { score: state.lastScore } : null,
      })),
    );
  }

  fetchSentimentTimeSeries(
    period: number,
    time?: string,
  ): Observable<SentimentTimeSeriesResult> {
    return this.scoreTimeSeriesService
      .getScoreTimeSeries({
        customParams: {
          collapse_on: ['participant'],
        },
        body: {
          score_type: { period },
          ...(time && { time }),
        },
      })
      .pipe(
        map((response) => {
          const timeSeries = response.score_ts?.[0]?.score_ts || [];
          return {
            quotient_ts: timeSeries.map((item) => ({
              date: item.date,
              quotient: item.score,
            })),
          };
        }),
        catchError(() => of({ quotient_ts: [] })),
      );
  }

  resolveScoreType(scoreId: string): ScoreRequestTypes {
    return this.scoreUtils.resolveScoreType(scoreId);
  }

  /**
   * Fetches entity sentiment data in a format compatible with isentiment endpoint.
   * Wrapper around NewsScoreStateService.fetchEntitySentiment for chart component usage.
   */
  fetchEntitySentiment(
    options: FetchEntitySentimentOptions,
  ): Observable<EntitySentimentResponse> {
    return this.newsScoreState.fetchEntitySentiment({
      ...options,
      collapseOn: true,
    });
  }
}
